import React from "react";

const AirQuality = ({ aqi, pm25, pm10, co, no2, o3, so2 }) => {
  const getAQIColor = (aqi) => {
    if (aqi <= 50) return "#4CAF50";
    if (aqi <= 100) return "#FFC107";
    if (aqi <= 150) return "#FF9800";
    if (aqi <= 200) return "#F44336";
    if (aqi <= 300) return "#9C27B0";
    return "#880E4F";
  };

  const getAQIDescription = (aqi) => {
    if (aqi <= 50)
      return {
        level: "Good",
        description:
          "Air quality is satisfactory, and air pollution poses little or no risk.",
      };
    if (aqi <= 100)
      return {
        level: "Moderate",
        description:
          "Air quality is acceptable. However, there may be a risk for some people, particularly those who are unusually sensitive to air pollution.",
      };
    if (aqi <= 150)
      return {
        level: "Unhealthy for Sensitive Groups",
        description:
          "Members of sensitive groups may experience health effects. The general public is less likely to be affected.",
      };
    if (aqi <= 200)
      return {
        level: "Unhealthy",
        description:
          "Some members of the general public may experience health effects; members of sensitive groups may experience more serious health effects.",
      };
    if (aqi <= 300)
      return {
        level: "Very Unhealthy",
        description:
          "Health alert: The risk of health effects is increased for everyone.",
      };
    return {
      level: "Hazardous",
      description:
        "Health warning of emergency conditions: everyone is more likely to be affected.",
    };
  };

  const getPollutantDescription = (pollutant, value) => {
    const descriptions = {
      pm25: "Fine particulate matter (≤2.5 µm)",
      pm10: "Coarse particulate matter (≤10 µm)",
      co: "Carbon monoxide",
      no2: "Nitrogen dioxide",
      o3: "Ozone",
      so2: "Sulfur dioxide",
    };
    return descriptions[pollutant];
  };

  const aqiColor = getAQIColor(aqi);
  const aqiInfo = getAQIDescription(aqi);

  return (
    <div className="bg-white rounded-lg p-4">
      {/* Main AQI Display */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="text-4xl font-bold" style={{ color: aqiColor }}>
            {aqi}
          </div>
          <div>
            <div className="font-medium" style={{ color: aqiColor }}>
              {aqiInfo.level}
            </div>
            <div className="text-gray-500">AQI</div>
          </div>
        </div>
      </div>

      {/* AQI Description */}
      <div className="mb-6 p-3 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">{aqiInfo.description}</p>
      </div>

      {/* Pollutants Grid */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-gray-500 mb-1">PM2.5</div>
          <div className="font-medium mb-1">{pm25} µg/m³</div>
          <div className="text-xs text-gray-600">
            {getPollutantDescription("pm25")}
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-gray-500 mb-1">PM10</div>
          <div className="font-medium mb-1">{pm10} µg/m³</div>
          <div className="text-xs text-gray-600">
            {getPollutantDescription("pm10")}
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-gray-500 mb-1">CO</div>
          <div className="font-medium mb-1">{co} ppm</div>
          <div className="text-xs text-gray-600">
            {getPollutantDescription("co")}
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-gray-500 mb-1">NO₂</div>
          <div className="font-medium mb-1">{no2} ppb</div>
          <div className="text-xs text-gray-600">
            {getPollutantDescription("no2")}
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-gray-500 mb-1">O₃</div>
          <div className="font-medium mb-1">{o3} ppb</div>
          <div className="text-xs text-gray-600">
            {getPollutantDescription("o3")}
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-gray-500 mb-1">SO₂</div>
          <div className="font-medium mb-1">{so2} ppb</div>
          <div className="text-xs text-gray-600">
            {getPollutantDescription("so2")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AirQuality;
